import {Inject, Injectable} from '@angular/core';
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {catchError, finalize, map, switchMap} from 'rxjs/operators';
import {Observable, of, throwError} from 'rxjs';
import {AUTH_CONFIG, AuthConfig, AuthService} from "./auth.service";
import {Router} from "@angular/router";

@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {

  isRefreshingToken = false;

  constructor(private authService: AuthService, @Inject(AUTH_CONFIG) private authConfig: AuthConfig,
              private router: Router, @Inject('environment') private environment: any) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any> | any> {
    //Skip interceptor for non Reloyaly api requests.
    if (!req.url.startsWith(this.environment.apiUrl)) {
      return next.handle(req);
    }
    return this.addAuthHeaders(req).pipe(
      switchMap(request => {
        return next.handle(request);
      }),
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          const httpErrorResponse = err as HttpErrorResponse;
          switch (httpErrorResponse.status) {
            case 401:
              return this.handle401Error(req, next, err);
            case 403:
              return this.handle403Error(err);
            default:
              return this.handleOtherErrors(err);
          }
        } else {
          return of(err);
        }
      }));
  }

  handle401Error(req: HttpRequest<any>, next: HttpHandler, err: HttpErrorResponse): Observable<HttpEvent<any> | any> {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;

      // Reset here so that the following requests wait until the token
      // comes back from the refreshToken call.

      return this.authService.refreshCurrentAccessToken().pipe(
        switchMap((newToken: string | null) => {
          if (newToken) {
            this.isRefreshingToken = false;
            return this.intercept(req, next);
          } else {
            return this.logout();
          }
        }), catchError(err => {
          return this.logout();
        }), finalize(() => {
          this.isRefreshingToken = false;
        }));
    } else {
      return this.intercept(req, next);
    }
  }

  private logout() {
    // If we don't get a new token, we are in trouble so logout.
    this.authService.logout().subscribe(() => {
      if (this.authConfig.notAuthenticatedRedirectRoute) {
        const _ = this.router.navigate([this.authConfig.notAuthenticatedRedirectRoute]);
      } else {
        const _ = this.router.navigate(['']);
      }
    });
    return throwError(() => 'Invalid refresh token!');
  }

  private addAuthHeaders(req: HttpRequest<any>): Observable<HttpRequest<any>> {
    if (this.authConfig?.publicEndpoints?.includes(req.url) ?? false) {
      return of(req);
    } else {
      return this.authService.getCurrentAccessToken().pipe(
        map(token => {
          if (token && !req.headers?.has('Authorization')) {
            return req.clone({
              setHeaders: {'Authorization': `Bearer ${token}`}
            });
          } else {
            return req;
          }
        })
      );
    }
  }

  private handle403Error(err: any) {

    return throwError(() => err);
  }

  private handleOtherErrors(err: any) {
    // TODO implement error handling
    return throwError(() => err);
  }
}
